import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Spin, Table, Input, Button } from 'antd';
import {
  ShopOutlined,
  DollarOutlined,
  TeamOutlined,
  CalculatorOutlined,
} from '@ant-design/icons';
import ReportService from '../../services/ReportService';
import StatCard from '../../components/StatCard';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('today');
  const [stats, setStats] = useState({
    SubeSayisi: 0,
    ToplamCiro: 0,
    KisiSayisi: 0,
    OrtalamaAdisyonTutari: 0
  });
  const [branchRevenue, setBranchRevenue] = useState([]);
  const [branchLoading, setBranchLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [searchText, setSearchText] = useState('');
  const [trends, setTrends] = useState({
    ciro: { percent: 0, dir: 'up', spark: [] },
    kisi: { percent: 0, dir: 'up', spark: [] },
    adisyon: { percent: 0, dir: 'up', spark: [] },
  });

  const calculateFontSize = (value) => {
    if (!isMobile) return undefined;

    const stringValue = value?.toString() || '';
    const length = stringValue.length;

    if (length <= 4) return { fontSize: '18px' };
    if (length <= 6) return { fontSize: '16px' };
    if (length <= 8) return { fontSize: '14px' };
    if (length <= 10) return { fontSize: '12px' };
    return { fontSize: '10px' };
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const token = localStorage.getItem("token");
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const companyCode = userDetails?.CompanyCode || '';

  const fetchDashboardStats = async (filter = dateFilter, setMainLoading = true) => {
    try {
      if (setMainLoading) setLoading(true);
      const response = await ReportService.dashboard.getDashboardStatistics({
        token,
        CompanyCode: companyCode,
        DateFilter: filter
      });

      if (response.status === 200) {
        setStats(response.data);
        setTrends({
          ciro: { percent: 3.4, dir: 'up', spark: [12, 14, 9, 15, 18, 16, 20] },
          kisi: { percent: 1.1, dir: 'down', spark: [10, 9, 11, 8, 7, 9, 8] },
          adisyon: { percent: 0.6, dir: 'up', spark: [5, 6, 6, 7, 6, 8, 9] },
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      if (setMainLoading) setLoading(false);
    }
  };

  const fetchBranchRevenue = async (filter = dateFilter) => {
    try {
      setBranchLoading(true);
      const response = await ReportService.dashboard.getBranchRevenueReport({
        token,
        CompanyCode: companyCode,
        DateFilter: filter
      });

      if (response.status === 200) {
        setBranchRevenue(response.data);
      }
    } catch (error) {
      console.error('Error fetching branch revenue:', error);
    } finally {
      setBranchLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardStats();
    fetchBranchRevenue();
  }, []);

  const handleFilterChange = (e) => {
    const newFilter = e.target.value;
    setDateFilter(newFilter);
    fetchDashboardStats(newFilter, false);
    fetchBranchRevenue(newFilter);
  };

  const branchColumns = [
    {
      title: <span className="text-base">Şube Adı</span>,
      dataIndex: 'SubeAdi',
      key: 'SubeAdi',
      width: isMobile ? '40%' : '50%',
      render: (text) => <span className="text-base">{text}</span>,
      ellipsis: isMobile
    },
    {
      title: <span className="text-base">Toplam Ciro</span>,
      dataIndex: 'ToplamCiro',
      key: 'ToplamCiro',
      width: isMobile ? '60%' : '50%',
      render: (value) => (
        <span className="text-base">
          {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value)}
        </span>
      ),
      sorter: (a, b) => a.ToplamCiro - b.ToplamCiro,
    },
  ];

  const filteredBranchRevenue = branchRevenue.filter((row) => {
    if (!searchText) return true;
    const needle = searchText.toLowerCase();
    return (
      String(row.SubeAdi || '').toLowerCase().includes(needle) ||
      String(row.SubeKodu || '').toLowerCase().includes(needle)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-50 via-white to-slate-50 border border-slate-200/60 shadow-lg backdrop-blur-sm">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-indigo-500/5"></div>
        <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-blue-400/10 to-transparent rounded-full blur-xl"></div>
        <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-indigo-400/10 to-transparent rounded-full blur-lg"></div>

        <div className="relative px-3 py-2 sm:px-4 sm:py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg sm:rounded-xl shadow-lg">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent leading-tight">
                Anasayfa
              </h1>
              <p className="text-slate-500 text-xs sm:text-sm font-medium">
                Genel bakış ve performans özetleri
              </p>
            </div>
          </div>

          <div className="flex bg-white/70 backdrop-blur-md rounded-lg sm:rounded-xl shadow-lg border border-white/50 overflow-hidden">
            {[
              { label: 'Bugün', value: 'today' },
              { label: 'Dün', value: 'yesterday' },
              { label: 'Hafta', value: 'week' },
              { label: 'Ay', value: 'month' },
              { label: 'Yıl', value: 'year' },
            ].map((option, index) => (
              <button
                key={option.value}
                onClick={() => handleFilterChange({ target: { value: option.value } })}
                className={`
                  relative px-2 py-1.5 sm:px-3 sm:py-2 text-xs font-semibold transition-all duration-300 ease-out
                  ${dateFilter === option.value
                    ? 'bg-gradient-to-r from-rose-500 to-red-600 text-white shadow-lg scale-105'
                    : 'text-slate-600 hover:text-slate-800 hover:bg-white/80'
                  }
                  ${index === 0 ? 'rounded-l-lg sm:rounded-l-xl' : ''}
                  ${index === 4 ? 'rounded-r-lg sm:rounded-r-xl' : ''}
                  ${dateFilter === option.value ? 'z-10' : 'z-0'}
                `}
              >
                {option.label}
                {dateFilter === option.value && (
                  <div className="absolute inset-0 bg-gradient-to-r from-rose-500 to-red-600 rounded-lg sm:rounded-xl -z-10 animate-pulse"></div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <Row gutter={[12, 12]} className="mt-3">
        <Col xs={12} sm={12} lg={6}>
          <StatCard
            title="Şube Sayısı"
            value={stats.SubeSayisi}
            suffix="şube"
            icon={<ShopOutlined />}
            gradientFrom="from-rose-100"
            gradientTo="to-white"
            isMobile={isMobile}
          />
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <StatCard
            title="Toplam Ciro"
            value={formatCurrency(stats.ToplamCiro)}
            suffix="₺"
            icon={<DollarOutlined />}
            gradientFrom="from-amber-100"
            gradientTo="to-white"
            isMobile={isMobile}
            trendPercent={trends.ciro.percent}
            trendDirection={trends.ciro.dir}
            sparklineData={trends.ciro.spark}
          />
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <StatCard
            title="Kişi Sayısı"
            value={stats.KisiSayisi}
            suffix="kişi"
            icon={<TeamOutlined />}
            gradientFrom="from-emerald-100"
            gradientTo="to-white"
            isMobile={isMobile}
            trendPercent={trends.kisi.percent}
            trendDirection={trends.kisi.dir}
            sparklineData={trends.kisi.spark}
          />
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <StatCard
            title="Adisyon Ort."
            value={formatCurrency(stats.OrtalamaAdisyonTutari)}
            suffix="₺"
            icon={<CalculatorOutlined />}
            gradientFrom="from-indigo-100"
            gradientTo="to-white"
            isMobile={isMobile}
            trendPercent={trends.adisyon.percent}
            trendDirection={trends.adisyon.dir}
            sparklineData={trends.adisyon.spark}
          />
        </Col>
      </Row>

      <div className="mt-4 sm:mt-6">
        <Card
          title={<h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold`}>Şube Bazlı Ciro Raporu</h2>}
          bordered={false}
          className="shadow-lg rounded-2xl border border-gray-100 bg-white"
          bodyStyle={{ padding: isMobile ? 12 : 18 }}
        >
          <div className="mb-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2">
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Input
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Şube adı/kodu ara"
                className="w-full sm:w-64"
              />
              <Button onClick={() => fetchBranchRevenue(dateFilter)} loading={branchLoading}>
                Yenile
              </Button>
            </div>
          </div>
          <Table
            columns={branchColumns}
            dataSource={filteredBranchRevenue}
            loading={branchLoading}
            rowKey="SubeKodu"
            sticky
            pagination={{
              defaultPageSize: isMobile ? 5 : 10,
              size: isMobile ? 'small' : 'default',
              showTotal: (total) => (
                <span className={`${isMobile ? 'text-sm' : 'text-base'}`}>Toplam {total} kayıt</span>
              ),
              showSizeChanger: !isMobile,
            }}
            className="branch-revenue-table [&_.ant-table-thead_tr_th]:bg-gray-50 [&_.ant-table-row:hover]:bg-rose-50/30"
            size={isMobile ? 'small' : 'middle'}
            rowClassName={() => (isMobile ? 'text-[13px]' : 'text-[15px]')}
            scroll={isMobile ? { x: true } : undefined}
            summary={(pageData) => {
              let total = 0;
              pageData.forEach((row) => {
                total += Number(row.ToplamCiro || 0);
              });
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}><span className="font-medium">Toplam</span></Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>
                      <span className="font-semibold">{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(total)}</span>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;